import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "../api";
import { useAuth } from "../AuthContext";
import { CLIENT_STATUSES, formatDate } from "../utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import DetailPanel from "../components/DetailPanel";
import Layout from "../components/Layout";
import { Plus, FileUp, Download, Users, Upload, Search, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useTrackedSearch, trackView } from "../activityTracker";

const POLL_MS = 10000;

// Normalise a Malaysian/foreign phone to a wa.me-safe digits string.
function waDigits(raw) {
  if (!raw) return "";
  let s = String(raw).trim();
  const hasPlus = s.startsWith("+");
  s = s.replace(/\D/g, "");
  if (hasPlus) return s;
  if (s.startsWith("0")) return "60" + s.slice(1);
  if (s.startsWith("60")) return s;
  return s;
}

// Light preview of the outreach — shown in a dialog before opening wa.me
// so the staff can customise per client.
function defaultOutreachMessage(client, senderName) {
  const company = client.company_name || "there";
  const contact = (client.contact_person || "").split(/\s+/)[0];
  const hello = contact ? `Hi ${contact}` : `Hi`;
  return (
    `${hello}, this is ${senderName || ""} from Mercy Consultancy.\n\n` +
    `We are a licensed labour recruitment agency specialising in foreign worker supply, ` +
    `permit renewals (VP(TE), PLKS), FOMEMA and Employment Pass processing for Malaysian businesses.\n\n` +
    `I was going through our list of ${company} and wanted to reach out — ` +
    `are you currently looking for workers, or do you have any permits coming up for renewal in the next few months?\n\n` +
    `Happy to send over our service list and rates if helpful.\n\n` +
    `Thanks!`
  );
}

export default function Clients() {
  const { user } = useAuth();
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  useTrackedSearch("clients", q);
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [waTarget, setWaTarget] = useState(null);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); setChecked(new Set()); }, [q, statusFilter, tab, pageSize]);

  const load = useCallback(async () => {
    const params = { limit: pageSize, skip: (page - 1) * pageSize };
    if (q) params.q = q;
    if (statusFilter !== "all") params.status = statusFilter;
    if (tab === "assigned") params.assigned_to = user?.id;
    if (tab === "customers") params.status = "Customer";
    if (tab === "dnc") params.status = "Do Not Call";
    const [c, s] = await Promise.all([api.get("/clients", { params }), api.get("/staff")]);
    setRows(c.data.rows || []);
    setTotal(c.data.total || 0);
    setStaff(s.data);
  }, [q, statusFilter, tab, user?.id, page, pageSize]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  const toggleCheck = (id) => {
    const next = new Set(checked);
    next.has(id) ? next.delete(id) : next.add(id);
    setChecked(next);
  };
  const toggleAll = () => setChecked(checked.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));

  const exportCsv = () => {
    const token = localStorage.getItem("mercy_token");
    const url = `${API}/clients/export`;
    // Use fetch+blob so we can set Authorization
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(b);
        link.download = "clients_export.csv";
        link.click();
      });
  };

  const isAdmin = user?.role === "admin";

  return (
    <Layout title="Clients">
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Clients</h1>
        <div className="flex items-center gap-2">
          {isAdmin && (<>
            <Button variant="outline" onClick={exportCsv} className="border-slate-700" data-testid="export-clients-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} className="border-slate-700" data-testid="import-clients-btn">
              <FileUp className="w-4 h-4 mr-1.5" /> Import CSV
            </Button>
          </>)}
          <Button onClick={() => setAddOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setChecked(new Set()); }}>
        <TabsList className="bg-transparent p-0 h-auto border-b hairline rounded-none">
          {[["all", "All Clients"], ["assigned", "My Assigned"], ["customers", "Customers"], ["dnc", "Do Not Call"]].map(([k, l]) => (
            <TabsTrigger key={k} value={k}
              className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500 text-slate-400 px-3 py-2">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div className="relative md:col-span-2">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company, phone, email, contact"
            className="pl-8 bg-[#0f1626] border-slate-800" data-testid="clients-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-[#0f1626] border-slate-800"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {CLIENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && checked.size > 0 && (
          <Button variant="outline" onClick={() => setBulkOpen(true)} className="border-blue-500/50 text-blue-300">
            <Users className="w-4 h-4 mr-1.5" /> Bulk actions ({checked.size})
          </Button>
        )}
      </div>

      <div className="hidden md:block rounded-lg border hairline surface-1 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b hairline">
              {isAdmin && <th className="w-8 pl-3"><Checkbox checked={checked.size === rows.length && rows.length > 0} onCheckedChange={toggleAll} /></th>}
              <th className="py-2.5 px-3">Company</th>
              <th className="px-3">Industry</th>
              <th className="px-3">Area</th>
              <th className="px-3 num">Rating</th>
              <th className="px-3">Language</th>
              <th className="px-3">Phone</th>
              <th className="px-3">Status</th>
              <th className="px-3">Assigned</th>
              <th className="px-3 num">Last contact</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="row-hover border-b border-slate-900/80 cursor-pointer"
                onClick={() => { setSelected(c); setOpen(true); trackView("client", c.id, c.company_name, "clients"); }} data-testid={`client-row-${c.id}`}>
                {isAdmin && (
                  <td className="pl-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={checked.has(c.id)} onCheckedChange={() => toggleCheck(c.id)} />
                  </td>
                )}
                <td className="py-2.5 px-3 font-medium">{c.company_name}</td>
                <td className="px-3 text-slate-300">{c.industry || "—"}</td>
                <td className="px-3 text-slate-300">{c.city || c.state || "—"}</td>
                <td className="px-3 num">
                  {c.rating ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="text-amber-300">★</span>
                      <span className="text-slate-200">{c.rating}</span>
                    </span>
                  ) : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-3 text-slate-300 text-xs">{c.language_tip || "—"}</td>
                <td className="px-3 num text-slate-300">
                  {c.phone ? (
                    <span className="flex items-center gap-1.5">
                      <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()}
                         className="hover:text-blue-400">{c.phone}</a>
                      <button type="button" title="WhatsApp — business outreach"
                        onClick={(e) => { e.stopPropagation(); setWaTarget(c); }}
                        className="text-emerald-400 hover:text-emerald-300"
                        data-testid={`client-wa-${c.id}`}>
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3"><span className="text-xs num px-2 py-0.5 rounded-full border hairline surface-2">{c.status}</span></td>
                <td className="px-3 text-slate-300" onClick={(e) => e.stopPropagation()}>
                  {isAdmin ? (
                    <InlineAssignSelect client={c} staff={staff}
                      onAssigned={(updated) => {
                        setRows((prev) => prev.map((r) => r.id === updated.id ? updated : r));
                      }} />
                  ) : (c.assigned_to_name || "—")}
                </td>
                <td className="px-3 num text-slate-400">{c.last_contacted ? formatDate(c.last_contacted) : "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={isAdmin ? 10 : 9} className="py-14 text-center text-slate-500">No clients yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {rows.map((c) => (
          <div key={c.id} className="rounded-lg border hairline surface-1 p-3"
            onClick={() => { setSelected(c); setOpen(true); trackView("client", c.id, c.company_name, "clients"); }}>
            <div className="font-medium">{c.company_name}</div>
            <div className="text-xs text-slate-400 mt-0.5">{c.industry || "—"} · {c.state || "—"}</div>
            <div className="text-xs num text-slate-500 mt-0.5">{c.phone || "—"}</div>
            <div className="mt-2 text-xs text-slate-400">{c.status}</div>
          </div>
        ))}
      </div>

      <DetailPanel open={open} onClose={() => setOpen(false)} entityType="client" entity={selected} onChanged={load} />
      <AddClientDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
      <ImportClientsDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <BulkActionsDialog open={bulkOpen} onOpenChange={setBulkOpen}
        ids={Array.from(checked)} staff={staff} onDone={() => { setChecked(new Set()); load(); }} />

      <Pagination
        page={page} pageSize={pageSize} total={total}
        onPage={setPage} onPageSize={setPageSize}
      />

      <ClientWhatsAppDialog
        client={waTarget}
        senderName={user?.display_name}
        onOpenChange={(v) => !v && setWaTarget(null)}
      />
    </div>
    </Layout>
  );
}

function AddClientDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    company_name: "", industry: "", state: "", city: "", phone: "", email: "",
    website: "", contact_person: "", status: "Not Contacted",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });
  const submit = async () => {
    if (!form.company_name) { toast.error("Company name is required"); return; }
    try { await api.post("/clients", form); toast.success("Client added"); onCreated?.(); onOpenChange(false);
      setForm({ company_name: "", industry: "", state: "", city: "", phone: "", email: "", website: "", contact_person: "", status: "Not Contacted" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg" data-testid="add-client-dialog">
        <DialogHeader><DialogTitle>Add client</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Company name *" v={form.company_name} on={set("company_name")} />
          <F label="Industry" v={form.industry} on={set("industry")} />
          <F label="State" v={form.state} on={set("state")} />
          <F label="City" v={form.city} on={set("city")} />
          <F label="Phone" v={form.phone} on={set("phone")} />
          <F label="Email" v={form.email} on={set("email")} />
          <F label="Website" v={form.website} on={set("website")} />
          <F label="Contact person" v={form.contact_person} on={set("contact_person")} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-500" data-testid="add-client-submit">Add client</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, v, on }) {
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <Input value={v} onChange={on} className="bg-[#0a0e1a] border-slate-800 mt-1" />
    </div>
  );
}

function ImportClientsDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setLoading(true);
    try {
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data); onDone?.();
      toast.success(`Imported ${data.imported}, updated ${data.updated}, skipped ${data.skipped}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setFile(null); setReport(null); } }}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg" data-testid="import-clients-dialog">
        <DialogHeader><DialogTitle>Import clients (CSV)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">
          Columns: <span className="num">company_name, industry, state, city, phone, email, website, contact_person, status</span>.
          Deduplicated by phone.
        </p>
        <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="bg-[#0a0e1a] border-slate-800" data-testid="import-clients-file" />
        {report && (
          <div className="rounded-md border hairline surface-2 p-3 text-sm space-y-1 num">
            <div className="text-emerald-300">Imported: {report.imported}</div>
            <div className="text-blue-300">Updated: {report.updated}</div>
            <div className="text-amber-300">Skipped: {report.skipped}</div>
            {report.errors?.length > 0 && (
              <div className="text-xs text-slate-400 mt-2 max-h-40 overflow-y-auto">
                {report.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={submit} disabled={!file || loading}
            className="bg-blue-600 hover:bg-blue-500" data-testid="import-clients-submit">
            <Upload className="w-4 h-4 mr-1.5" />{loading ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkActionsDialog({ open, onOpenChange, ids, staff, onDone }) {
  const [action, setAction] = useState("assign");
  const [assignedTo, setAssignedTo] = useState("");
  const [status, setStatus] = useState("Do Not Call");
  const submit = async () => {
    try {
      if (action === "assign") await api.post("/clients/bulk-assign", { ids, assigned_to: assignedTo || null });
      else await api.post("/clients/bulk-status", { ids, status });
      toast.success(`Applied to ${ids.length} clients`);
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader><DialogTitle>Bulk actions</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="bg-[#0a0e1a] border-slate-800"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="assign">Assign to staff</SelectItem>
              <SelectItem value="status">Change status</SelectItem>
            </SelectContent>
          </Select>
          {action === "assign" ? (
            <Select value={assignedTo || "unassigned"} onValueChange={(v) => setAssignedTo(v === "unassigned" ? "" : v)}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800"><SelectValue placeholder="Select staff" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">— Unassign —</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800"><SelectValue /></SelectTrigger>
              <SelectContent>{CLIENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-500">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Pagination({ page, pageSize, total, onPage, onPageSize }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap pt-2" data-testid="clients-pagination">
      <div className="text-xs num text-slate-500">
        Showing <span className="text-slate-200">{start.toLocaleString()}–{end.toLocaleString()}</span> of{" "}
        <span className="text-slate-200">{total.toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-2">
        <Select value={String(pageSize)} onValueChange={(v) => onPageSize(Number(v))}>
          <SelectTrigger className="bg-[#0f1626] border-slate-800 h-8 w-[110px]" data-testid="clients-page-size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[25, 50, 100, 200, 500].map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="border-slate-700 h-8"
          disabled={page <= 1} onClick={() => onPage(1)} data-testid="clients-page-first">« First</Button>
        <Button size="sm" variant="outline" className="border-slate-700 h-8"
          disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="clients-page-prev">‹ Prev</Button>
        <span className="text-xs num text-slate-400 px-1">
          Page <span className="text-slate-200">{page}</span> / {totalPages}
        </span>
        <Button size="sm" variant="outline" className="border-slate-700 h-8"
          disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid="clients-page-next">Next ›</Button>
        <Button size="sm" variant="outline" className="border-slate-700 h-8"
          disabled={page >= totalPages} onClick={() => onPage(totalPages)} data-testid="clients-page-last">Last »</Button>
      </div>
    </div>
  );
}

function ClientWhatsAppDialog({ client, senderName, onOpenChange }) {
  const [text, setText] = useState("");
  useEffect(() => {
    if (client) setText(defaultOutreachMessage(client, senderName));
  }, [client, senderName]);

  const send = () => {
    const digits = waDigits(client?.phone);
    if (!digits) { toast.error("No valid phone number"); return; }
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch {}
  };

  return (
    <Dialog open={!!client} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-400" />
            WhatsApp — {client?.company_name}
            <span className="text-xs num text-slate-500 ml-auto">{client?.phone}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs text-slate-400">Business outreach message — edit before sending</Label>
          <Textarea value={text} onChange={(e) => setText(e.target.value)}
            className="bg-[#0a0e1a] border-slate-800 min-h-[240px] text-sm leading-relaxed"
            data-testid="client-wa-text" />
        </div>
        <DialogFooter>
          <Button variant="outline" className="border-slate-700" onClick={copy}
            data-testid="client-wa-copy">Copy</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={send}
            data-testid="client-wa-send">
            <MessageCircle className="w-4 h-4 mr-1" /> Open WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function InlineAssignSelect({ client, staff, onAssigned }) {
  const [saving, setSaving] = React.useState(false);
  const value = client.assigned_to || "__unassigned";
  const activeStaff = (staff || []).filter((u) => u.is_active !== false);
  const change = async (v) => {
    const newAssigned = v === "__unassigned" ? null : v;
    if ((newAssigned || null) === (client.assigned_to || null)) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${client.id}`, { assigned_to: newAssigned });
      onAssigned?.(data);
      toast.success(newAssigned
        ? `Assigned to ${data.assigned_to_name}`
        : "Unassigned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <Select value={value} onValueChange={change} disabled={saving}>
      <SelectTrigger
        data-testid={`client-assign-${client.id}`}
        className="h-7 px-2 text-xs bg-transparent border-slate-800 hover:border-blue-500/60 w-[150px]">
        <SelectValue>
          {client.assigned_to_name ? (
            <span className="flex items-center gap-1.5 truncate">
              <span className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: client.assigned_to_color || "#64748b" }} />
              <span className="truncate">{client.assigned_to_name}</span>
            </span>
          ) : <span className="text-slate-500">— Unassigned</span>}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-[280px]">
        <SelectItem value="__unassigned">— Unassigned</SelectItem>
        {activeStaff.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full"
                style={{ background: u.color || "#64748b" }} />
              {u.display_name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
